export interface StreamChunk {
  content: string;
  sequenceNumber: number;
}

/**
 * Manages streaming chunk sequence tracking.
 * Ensures chunks are processed in the correct order regardless of delivery timing.
 *
 * Contract:
 * - The initial connection always sends a chunk (even if empty with sequenceNumber 0)
 * - Subsequent chunks have incrementing sequence numbers (1, 2, 3, ...)
 *
 * Features:
 * - Sequential chunk processing with gap detection
 * - Out-of-order chunk caching until gaps are filled
 * - Automatic cache cleanup to prevent memory leaks
 */
const MAX_CACHED_CHUNKS = 100;

export interface StreamSequenceAccumulatorOptions {
  /** When set, the accumulator expects this as the first sequence number
   *  instead of accepting whatever arrives first. Out-of-order chunks
   *  are cached until the expected sequence arrives. */
  initialSequenceNumber?: number;
}

export class StreamSequenceAccumulator {
  private content = '';
  private expectedSequenceNumber: number | null = null;
  private cachedChunks: Map<number, StreamChunk> = new Map();

  constructor(options?: StreamSequenceAccumulatorOptions) {
    if (options?.initialSequenceNumber !== undefined) {
      this.expectedSequenceNumber = options.initialSequenceNumber;
    }
  }

  getContent(): string {
    return this.content;
  }

  processChunk(chunk: StreamChunk): void {
    const {sequenceNumber, content} = chunk;

    // When expectedSequenceNumber is null (no initialSequenceNumber provided),
    // accept whatever arrives first. This supports rejoining an in-progress
    // stream where the backend sends accumulated content at a non-zero sequence.
    if (this.expectedSequenceNumber === null) {
      this.content = content;
      this.expectedSequenceNumber = sequenceNumber + 1;
      return;
    }

    // Handle reconnect catch-up chunk:
    // On reconnect, backend sends full accumulated content (not delta).
    // Detect this by checking if the chunk is out-of-order, longer than what
    // we have, and contains our content as a prefix (proving it's a snapshot,
    // not a large delta that happens to be longer early in the stream).
    // Guard: skip when content is empty — startsWith('') is always true,
    // so any out-of-order chunk would falsely match.
    if (
      this.content.length > 0 &&
      sequenceNumber > this.expectedSequenceNumber &&
      content.length > this.content.length &&
      content.startsWith(this.content)
    ) {
      this.content = content;
      this.cachedChunks.clear();
      this.expectedSequenceNumber = sequenceNumber + 1;
      return;
    }

    // Reject chunks that don't match expected sequence
    if (sequenceNumber !== this.expectedSequenceNumber) {
      // Only cache future chunks; ignore stale/duplicate chunks
      if (sequenceNumber > this.expectedSequenceNumber) {
        if (this.cachedChunks.size >= MAX_CACHED_CHUNKS) {
          // Cache overflow — skip ahead to highest available sequence
          this.cachedChunks.clear();
          return;
        }
        this.cachedChunks.set(sequenceNumber, chunk);
      }
      return;
    }

    // Handle in-order chunk, process it and flush cache if needed
    this.processChunkInSequence(chunk);
    this.processCachedChunks(this.expectedSequenceNumber);
  }

  reset(options?: StreamSequenceAccumulatorOptions): void {
    this.content = '';
    this.expectedSequenceNumber =
      options?.initialSequenceNumber !== undefined
        ? options.initialSequenceNumber
        : null;
    this.cachedChunks.clear();
  }

  private processChunkInSequence(chunk: StreamChunk): void {
    this.content += chunk.content;
    this.expectedSequenceNumber = chunk.sequenceNumber + 1;
  }

  private processCachedChunks(startingSequence: number): void {
    let currentSequence = startingSequence;

    while (this.cachedChunks.has(currentSequence)) {
      const chunk = this.cachedChunks.get(currentSequence)!;
      this.processChunkInSequence(chunk);
      this.cachedChunks.delete(currentSequence);
      currentSequence++;
    }
  }
}
